import {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import api from '../services/api';

export const STUDIO_IDS = [3, 2, 1];
export const EMPTY_ACTION_COUNTS = Object.fromEntries(
  STUDIO_IDS.map(id => [id, {newCount: 0, changeCount: 0, total: 0}])
);
export const needsNewConsultation = item =>
  item.status === 'waiting' && (item.invitation_state === 'reschedule_requested' || item.price_reply_requested === true);
export const actionSummary = counts => STUDIO_IDS.reduce((sum, id) => ({
  newCount: sum.newCount + (counts[id]?.newCount || 0),
  changeCount: sum.changeCount + (counts[id]?.changeCount || 0),
  cancelCount: sum.cancelCount + (counts[id]?.cancelCount || 0),
  confirmCount: sum.confirmCount + (counts[id]?.confirmCount || 0),
  total: sum.total + (counts[id]?.total || 0),
}), {newCount: 0, changeCount: 0, cancelCount: 0, confirmCount: 0, total: 0});
export const actionLabel = (name, count) =>
  `${name}: ${count.newCount} neue Anfragen, ${count.changeCount} Terminwünsche, ${count.cancelCount || 0} ungesehene Stornierungen, ${count.confirmCount || 0} ungesehene Bestätigungen`;
export const actionTarget = counts => {
  const id = STUDIO_IDS.find(id => counts[id]?.changeCount > 0) ||
    STUDIO_IDS.find(id => counts[id]?.newCount > 0);
  if (!id && STUDIO_IDS.some(id => counts[id]?.cancelCount > 0)) return '/consultations?cancellations=1';
  if (!id && STUDIO_IDS.some(id => counts[id]?.confirmCount > 0)) return '/consultations?confirmations=1';
  return `/consultations?tasks=1${id ? `&locationId=${id}` : ''}`;
};

// A single polling source is shared by desktop navigation and D/M/E.
export default function useConsultationActionCounts(enabled = true, cancellationCounts = {}, confirmationCounts = {}) {
  const location = useLocation();
  const [counts, setCounts] = useState(EMPTY_ACTION_COUNTS);
  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let controller;
    const refresh = async () => {
      controller?.abort();
      const request = new AbortController();
      controller = request;
      const results = await Promise.allSettled([
        api.get('/api/consultations/counts-by-location', {signal: request.signal}),
        ...STUDIO_IDS.map(id => api.get('/api/consultations', {
          params: {locationId: id, status: 'waiting'}, signal: request.signal,
        })),
      ]);
      if (stopped || request.signal.aborted) return;
      setCounts(previous => {
        const raw = results[0].status === 'fulfilled'
          ? results[0].value.data?.counts || results[0].value.data || {} : null;
        return Object.fromEntries(STUDIO_IDS.map((id, index) => {
          const result = results[index + 1];
          const newCount = raw ? Math.max(0, Number(raw[id]) || 0) : previous[id].newCount;
          const changeCount = result.status === 'fulfilled' && Array.isArray(result.value.data)
            ? result.value.data.filter(needsNewConsultation).length : previous[id].changeCount;
          return [id, {newCount, changeCount, total: newCount + changeCount}];
        }));
      });
      for (const result of results) {
        if (result.status === 'rejected') console.error('Aufgabenzähler konnte nicht aktualisiert werden:', result.reason);
      }
    };
    const visibleRefresh = () => {if (document.visibilityState === 'visible') void refresh();};
    void refresh();
    const timer = setInterval(visibleRefresh, 15000);
    window.addEventListener('consultations-changed', visibleRefresh);
    window.addEventListener('focus', visibleRefresh);
    document.addEventListener('visibilitychange', visibleRefresh);
    return () => {
      stopped = true; controller?.abort(); clearInterval(timer);
      window.removeEventListener('consultations-changed', visibleRefresh);
      window.removeEventListener('focus', visibleRefresh);
      document.removeEventListener('visibilitychange', visibleRefresh);
    };
  }, [enabled, location.key]);
  return Object.fromEntries(STUDIO_IDS.map(id => {
    const base = enabled ? counts[id] : EMPTY_ACTION_COUNTS[id];
    const cancelCount = enabled ? Math.max(0, Number(cancellationCounts[id]) || 0) : 0;
    const confirmCount = enabled ? Math.max(0, Number(confirmationCounts[id]) || 0) : 0;
    return [id, {...base, cancelCount, confirmCount, total: base.newCount + base.changeCount + cancelCount + confirmCount}];
  }));
}
// prime-ink-cancellation-alerts-v1
// prime-ink-confirmation-notifications-v1
